import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { fetch } from '../../../utils/api-wrapper';

export const getCardTypeForUpload = (transactionType) => {
  if (transactionType === 'expense_invoice' || transactionType === 'personal-card') return 'personal-card';
  return 'p-card';
};

export const buildStatementUploadUrl = (statementId, cardType, transactionType) => {
  const query = new URLSearchParams({ card_type: cardType, transaction_type: transactionType });
  return `/c_card/statement/add/${statementId}?${query.toString()}`;
};

export default function useUploadsWithAutoMatch({ statementId, t, getDefaultTransactionType, fetchStatement, hasUnprocessedCompletedExpenses }) {
  const [dialogIsOpen, setDialogIsOpen] = useState(false);
  const isMountedRef = useRef(true);
  const terminalStatuses = new Set(['completed', 'need_attention']);

  const refreshIntervalRef = useRef(null);
  const refreshTimeoutRef = useRef(null);
  const autoRefreshActiveRef = useRef(false);

  const openUploadModal = () => setDialogIsOpen(true);
  const closeDialog = () => setDialogIsOpen(false);

  const stopAutoRefresh = () => {
    if (!autoRefreshActiveRef.current && !refreshIntervalRef.current && !refreshTimeoutRef.current) return;
    autoRefreshActiveRef.current = false;

    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }
  };

  const startAutoRefresh = () => {
    const MAX_DURATION = 5 * 60 * 1000; // 5 minutes
    const REFRESH_INTERVAL = 10 * 1000; // 10 seconds

    stopAutoRefresh();
    if (autoRefreshActiveRef.current) return;

    autoRefreshActiveRef.current = true;

    refreshIntervalRef.current = setInterval(async () => {
      if (!autoRefreshActiveRef.current || !isMountedRef.current) return;

      const statement = await fetchStatement();
      if (!isMountedRef.current || !statement?.expenses?.length) {
        return;
      }

      // If there are any newly completed expenses, process them.
      if (hasUnprocessedCompletedExpenses?.(statement)) {
        await fetchStatement();
      }

      if (!isMountedRef.current) return;

      // Stop if there are no more unprocessed completed expenses (all done or none will appear).
      if (!hasUnprocessedCompletedExpenses?.(statement)) {
        // Determine if all expenses are in a terminal status (completed or need_attention)
        const allTerminal =
          statement.expenses.length > 0 && statement.expenses.every((e) => e?.status && terminalStatuses.has(String(e.status).toLowerCase().trim()));
        if (allTerminal) {
          stopAutoRefresh();
        }
      }
    }, REFRESH_INTERVAL);

    refreshTimeoutRef.current = setTimeout(() => {
      stopAutoRefresh();
    }, MAX_DURATION);
  };

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      stopAutoRefresh();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const uploadFile = (wrappedFile) => {
    const formData = new FormData();
    for (let pair of wrappedFile.formData.entries()) {
      formData.append(pair[0], pair[1]);
    }

    const transactionType = wrappedFile?.rawFile?.transactionType || getDefaultTransactionType?.() || 'expense_invoice';
    const cardType = getCardTypeForUpload(transactionType);

    return new Promise((resolve) => {
      fetch(
        buildStatementUploadUrl(statementId, cardType, transactionType),
        'PUT',
        formData,
        () => {
          resolve({ success: true, file: wrappedFile });
        },
        (error) => {
          let message = error?.response?.data?.message || error?.message;
          if (error?.response?.data?.message === 'duplicate') {
            message = t.read('duplicate_file');
          }
          toast.error(message);
          resolve({ success: false, file: wrappedFile });
        },
        true // isFormData
      );
    });
  };

  const submitFiles = async (files) => {
    try {
      const results = await Promise.all(files.map((f) => uploadFile(f)));
      const failed = results.filter((r) => !r.success);

      if (failed.length === 0) {
        toast.success(t.read('cc_transactions.files_uploaded') || 'Files uploaded successfully');
      }

      if (isMountedRef.current) setDialogIsOpen(false);

      const latest = await fetchStatement();
      if (!isMountedRef.current) return;
      
      const hasExpenses = !!latest?.expenses?.length;
      const hasIncomplete = hasExpenses && latest.expenses.some((e) => !e.status || !terminalStatuses.has(e.status.toLowerCase()));

      // Start auto refresh if: no expenses yet (processing pending), OR there are incomplete ones, OR we just processed some but more might appear.
      if ((!hasExpenses || hasIncomplete) && isMountedRef.current) {
        startAutoRefresh();
      }
    } catch {
      toast.error(t.read('cc_transactions.error_uploading_files') || 'Error uploading files');
    } finally {
      if (isMountedRef.current) setDialogIsOpen(false);
    }
  };

  return {
    dialogIsOpen,
    openUploadModal,
    closeDialog,
    submitFiles,
    startAutoRefresh,
    stopAutoRefresh,
    isAutoRefreshingActive: autoRefreshActiveRef.current,
  };
}
